import React, { useState, useEffect, useCallback } from 'react';
import {
    Page,
    Layout,
    Card,
    BlockStack,
    InlineStack,
    Text,
    Button,
    Spinner,
    Banner,
    Badge,
    DataTable,
    Modal,
    FormLayout,
    TextField,
    Select,
    ProgressBar,
    Tabs
} from '@shopify/polaris';
import { budgetService } from '../services/budgetService';
import {
    accountingHelpers,
    getExpenseCategoryOptions,
    getRecurringFrequencyOptions,
    RECURRING_STATUSES,
    RECURRING_FREQUENCIES,
    getBudgetHealthStatus
} from '../constants/accountingConstants';
import KPICard from '../components/accounting/KPICard';

const BudgetsPage = () => {
    // State
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [budgetData, setBudgetData] = useState({ budgets: [], totals: {} });
    const [recurringExpenses, setRecurringExpenses] = useState([]);
    const [dueRecurring, setDueRecurring] = useState([]);

    // Period selector
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);

    // Tab state
    const [selectedTab, setSelectedTab] = useState(0);

    // Modal state
    const [showBudgetModal, setShowBudgetModal] = useState(false);
    const [showRecurringModal, setShowRecurringModal] = useState(false);
    const [budgetForm, setBudgetForm] = useState({ category: '', amount: '' });
    const [recurringForm, setRecurringForm] = useState({
        title: '',
        category: '',
        amount: '',
        frequency: 'monthly',
        start_date: new Date().toISOString().split('T')[0],
        auto_approve: false
    });
    const [submitting, setSubmitting] = useState(false);

    // Load data
    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const [budgetRes, recurringRes, dueRes] = await Promise.all([
                budgetService.getBudgetsForPeriod(year, month),
                budgetService.getRecurringExpenses({}),
                budgetService.getDueRecurringExpenses()
            ]);

            setBudgetData(budgetRes.data || { budgets: [], totals: {} });
            setRecurringExpenses(recurringRes.data || []);
            setDueRecurring(dueRes.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [year, month]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Handle set budget
    const handleSetBudget = async () => {
        setSubmitting(true);
        try {
            await budgetService.setBudget(
                budgetForm.category,
                year,
                month,
                parseFloat(budgetForm.amount)
            );
            setSuccessMessage('Budget set successfully');
            setShowBudgetModal(false);
            setBudgetForm({ category: '', amount: '' });
            loadData();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    // Handle create recurring
    const handleCreateRecurring = async () => {
        setSubmitting(true);
        try {
            await budgetService.createRecurringExpense({
                ...recurringForm,
                amount: parseFloat(recurringForm.amount)
            });
            setSuccessMessage('Recurring expense created successfully');
            setShowRecurringModal(false);
            setRecurringForm({
                title: '',
                category: '',
                amount: '',
                frequency: 'monthly',
                start_date: new Date().toISOString().split('T')[0],
                auto_approve: false
            });
            loadData();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    // Handle process recurring
    const handleProcessRecurring = async () => {
        setSubmitting(true);
        try {
            const result = await budgetService.processRecurringExpenses();
            setSuccessMessage(`Processed ${result.data?.processed || 0} recurring expenses`);
            loadData();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    // Handle pause/resume/cancel recurring
    const handleRecurringAction = async (id, action) => {
        setSubmitting(true);
        try {
            if (action === 'pause') await budgetService.pauseRecurring(id);
            else if (action === 'resume') await budgetService.resumeRecurring(id);
            else if (action === 'cancel') await budgetService.cancelRecurring(id);

            setSuccessMessage(`Recurring expense ${action}d successfully`);
            loadData();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    // Render budgets table
    const renderBudgetsTable = () => {
        const budgets = budgetData.budgets || [];

        if (budgets.length === 0) {
            return <Text as="p" tone="subdued">No budgets set for this period</Text>;
        }

        const rows = budgets.map(b => {
            const health = getBudgetHealthStatus(b.utilization_percent || 0);
            return [
                accountingHelpers.getExpenseCategoryLabel(b.category),
                accountingHelpers.formatCurrency(b.amount),
                accountingHelpers.formatCurrency(b.spent || 0),
                accountingHelpers.formatCurrency(b.remaining),
                <InlineStack gap="200" blockAlign="center">
                    <div style={{ width: '100px' }}>
                        <ProgressBar
                            progress={Math.min(b.utilization_percent || 0, 100)}
                            tone={b.is_over_budget ? 'critical' : b.utilization_percent >= 80 ? 'warning' : 'success'}
                            size="small"
                        />
                    </div>
                    <Text as="span" variant="bodySm">{(b.utilization_percent || 0).toFixed(0)}%</Text>
                </InlineStack>,
                <Badge tone={health.badge}>{health.label}</Badge>
            ];
        });

        return (
            <DataTable
                columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'text', 'text']}
                headings={['Category', 'Budget', 'Spent', 'Remaining', 'Utilization', 'Status']}
                rows={rows}
            />
        );
    };

    // Render recurring table
    const renderRecurringTable = () => {
        if (recurringExpenses.length === 0) {
            return <Text as="p" tone="subdued">No recurring expenses configured</Text>;
        }

        const rows = recurringExpenses.map(r => {
            const statusConfig = RECURRING_STATUSES[r.status?.toUpperCase()] || RECURRING_STATUSES.ACTIVE;
            return [
                r.title,
                accountingHelpers.getExpenseCategoryLabel(r.category),
                accountingHelpers.formatCurrency(r.amount),
                RECURRING_FREQUENCIES[r.frequency?.toUpperCase()]?.label || r.frequency,
                r.next_due_date ? new Date(r.next_due_date).toLocaleDateString() : '-',
                <Badge tone={statusConfig.badge}>{statusConfig.label}</Badge>,
                <InlineStack gap="100">
                    {r.status === 'active' && (
                        <Button size="slim" onClick={() => handleRecurringAction(r._id, 'pause')}>Pause</Button>
                    )}
                    {r.status === 'paused' && (
                        <Button size="slim" onClick={() => handleRecurringAction(r._id, 'resume')}>Resume</Button>
                    )}
                    {r.status !== 'cancelled' && (
                        <Button size="slim" tone="critical" onClick={() => handleRecurringAction(r._id, 'cancel')}>Cancel</Button>
                    )}
                </InlineStack>
            ];
        });

        return (
            <DataTable
                columnContentTypes={['text', 'text', 'numeric', 'text', 'text', 'text', 'text']}
                headings={['Title', 'Category', 'Amount', 'Frequency', 'Next Due', 'Status', 'Actions']}
                rows={rows}
            />
        );
    };

    // Generate year/month options
    const yearOptions = [];
    for (let y = now.getFullYear(); y >= now.getFullYear() - 2; y--) {
        yearOptions.push({ label: String(y), value: String(y) });
    }

    const monthOptions = [
        { label: 'January', value: '1' }, { label: 'February', value: '2' },
        { label: 'March', value: '3' }, { label: 'April', value: '4' },
        { label: 'May', value: '5' }, { label: 'June', value: '6' },
        { label: 'July', value: '7' }, { label: 'August', value: '8' },
        { label: 'September', value: '9' }, { label: 'October', value: '10' },
        { label: 'November', value: '11' }, { label: 'December', value: '12' }
    ];

    const tabs = [
        { id: 'budgets', content: 'Budgets', panelID: 'budgets-panel' },
        { id: 'recurring', content: 'Recurring Expenses', panelID: 'recurring-panel' }
    ];

    if (loading) {
        return (
            <Page title="Budgets & Recurring Expenses">
                <Layout>
                    <Layout.Section>
                        <Card><InlineStack align="center"><Spinner size="large" /></InlineStack></Card>
                    </Layout.Section>
                </Layout>
            </Page>
        );
    }

    const totals = budgetData.totals || {};

    return (
        <Page
            title="Budgets & Recurring Expenses"
            subtitle="Manage expense budgets and automate recurring expenses"
            primaryAction={{
                content: selectedTab === 0 ? 'Set Budget' : 'Add Recurring',
                onAction: () => selectedTab === 0 ? setShowBudgetModal(true) : setShowRecurringModal(true)
            }}
            secondaryActions={dueRecurring.length > 0 ? [{
                content: `Process ${dueRecurring.length} Due`,
                onAction: handleProcessRecurring,
                loading: submitting
            }] : []}
        >
            <Layout>
                {error && (
                    <Layout.Section>
                        <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>
                    </Layout.Section>
                )}

                {successMessage && (
                    <Layout.Section>
                        <Banner tone="success" onDismiss={() => setSuccessMessage(null)}>{successMessage}</Banner>
                    </Layout.Section>
                )}

                {/* Period Selector */}
                <Layout.Section>
                    <Card>
                        <InlineStack gap="400" blockAlign="end">
                            <Select label="Year" options={yearOptions} value={String(year)} onChange={(v) => setYear(parseInt(v))} />
                            <Select label="Month" options={monthOptions} value={String(month)} onChange={(v) => setMonth(parseInt(v))} />
                            <Button onClick={loadData}>Load</Button>
                        </InlineStack>
                    </Card>
                </Layout.Section>

                {/* Summary Cards */}
                <Layout.Section>
                    <InlineStack gap="400">
                        <KPICard title="Total Budget" value={totals.total_budget || 0} format="currency" />
                        <KPICard title="Total Spent" value={totals.total_spent || 0} format="currency" />
                        <KPICard title="Total Remaining" value={totals.total_remaining || 0} format="currency" />
                        <KPICard
                            title="Over Budget"
                            value={totals.over_budget_categories?.length || 0}
                            format="number"
                            subtitle="Categories"
                        />
                    </InlineStack>
                </Layout.Section>

                {/* Tabs */}
                <Layout.Section>
                    <Card>
                        <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
                            <BlockStack gap="400">
                                {selectedTab === 0 ? renderBudgetsTable() : renderRecurringTable()}
                            </BlockStack>
                        </Tabs>
                    </Card>
                </Layout.Section>
            </Layout>

            {/* Set Budget Modal */}
            <Modal
                open={showBudgetModal}
                onClose={() => setShowBudgetModal(false)}
                title="Set Category Budget"
                primaryAction={{
                    content: 'Set Budget',
                    onAction: handleSetBudget,
                    loading: submitting,
                    disabled: !budgetForm.category || !budgetForm.amount
                }}
                secondaryActions={[{ content: 'Cancel', onAction: () => setShowBudgetModal(false) }]}
            >
                <Modal.Section>
                    <FormLayout>
                        <Select
                            label="Category"
                            options={[{ label: 'Select category...', value: '' }, ...getExpenseCategoryOptions()]}
                            value={budgetForm.category}
                            onChange={(v) => setBudgetForm({ ...budgetForm, category: v })}
                        />
                        <TextField
                            label="Budget Amount"
                            type="number"
                            value={budgetForm.amount}
                            onChange={(v) => setBudgetForm({ ...budgetForm, amount: v })}
                            prefix="EGP"
                            autoComplete="off"
                        />
                    </FormLayout>
                </Modal.Section>
            </Modal>

            {/* Create Recurring Modal */}
            <Modal
                open={showRecurringModal}
                onClose={() => setShowRecurringModal(false)}
                title="Create Recurring Expense"
                primaryAction={{
                    content: 'Create',
                    onAction: handleCreateRecurring,
                    loading: submitting,
                    disabled: !recurringForm.title || !recurringForm.category || !recurringForm.amount
                }}
                secondaryActions={[{ content: 'Cancel', onAction: () => setShowRecurringModal(false) }]}
            >
                <Modal.Section>
                    <FormLayout>
                        <TextField
                            label="Title"
                            value={recurringForm.title}
                            onChange={(v) => setRecurringForm({ ...recurringForm, title: v })}
                            autoComplete="off"
                        />
                        <Select
                            label="Category"
                            options={[{ label: 'Select category...', value: '' }, ...getExpenseCategoryOptions()]}
                            value={recurringForm.category}
                            onChange={(v) => setRecurringForm({ ...recurringForm, category: v })}
                        />
                        <TextField
                            label="Amount"
                            type="number"
                            value={recurringForm.amount}
                            onChange={(v) => setRecurringForm({ ...recurringForm, amount: v })}
                            prefix="EGP"
                            autoComplete="off"
                        />
                        <Select
                            label="Frequency"
                            options={getRecurringFrequencyOptions()}
                            value={recurringForm.frequency}
                            onChange={(v) => setRecurringForm({ ...recurringForm, frequency: v })}
                        />
                        <TextField
                            label="Start Date"
                            type="date"
                            value={recurringForm.start_date}
                            onChange={(v) => setRecurringForm({ ...recurringForm, start_date: v })}
                            autoComplete="off"
                        />
                    </FormLayout>
                </Modal.Section>
            </Modal>
        </Page>
    );
};

export default BudgetsPage;
